import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import SoundStrip from "../components/SoundStrip";

export default function Pillar() {
    const { pillarId } = useParams();
    const [pillar, setPillar] = useState(null);
    const [completed, setCompleted] = useState(new Set());

    useEffect(() => {
        api.get(`/curriculum/${pillarId}`).then((r) => setPillar(r.data));
        api.get("/progress/me").then((r) => setCompleted(new Set(r.data.completed_lessons || [])));
    }, [pillarId]);

    if (!pillar) return <main className="container-page py-16">Loading…</main>;

    return (
        <main className="container-page py-12 fade-up">
            <Link to="/app/curriculum" className="inline-flex items-center gap-2 text-[#515B57] hover:text-[#2D5D4E] mb-6" data-testid="pillar-back">
                <ArrowLeft className="w-4 h-4" /> All pillars
            </Link>
            <p className="label-eyebrow mb-2">Step {pillar.order}</p>
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }} data-testid="pillar-title">
                {pillar.title}
            </h1>
            <p className="text-xl text-[#515B57] max-w-2xl mb-3">{pillar.subtitle}</p>
            <p className="text-lg text-[#515B57] max-w-3xl leading-relaxed mb-12">{pillar.summary}</p>

            {pillar.id === "phonics" && <SoundStrip />}

            <div className="space-y-4">
                {[...pillar.lessons].sort((a, b) => (a.level || 1) - (b.level || 1)).map((l, i) => {
                    const done = completed.has(l.id);
                    return (
                        <Link
                            key={l.id}
                            to={`/app/curriculum/${pillar.id}/lesson/${l.id}`}
                            data-testid={`lesson-card-${l.id}`}
                            className="card-rw p-6 flex items-center gap-5 hover:-translate-y-0.5"
                        >
                            {done ? (
                                <CheckCircle2 className="w-7 h-7 text-[#3A7D5C] flex-shrink-0" strokeWidth={1.6} />
                            ) : (
                                <Circle className="w-7 h-7 text-[#819888] flex-shrink-0" strokeWidth={1.6} />
                            )}
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                    <p className="label-eyebrow">Lesson {i + 1}</p>
                                    {l.level && (
                                        <span
                                            className="text-[0.65rem] tracking-[0.1em] uppercase font-medium px-2 py-0.5 rounded"
                                            style={{
                                                background: ["#E0EDE6", "#E8E2D2", "#FDE9DD", "#F3DDD5"][l.level - 1] || "#E0EDE6",
                                                color: ["#2D5D4E", "#1F4237", "#B86644", "#7D2F2F"][l.level - 1] || "#2D5D4E",
                                            }}
                                            data-testid={`lesson-level-${l.id}`}
                                        >
                                            {["Beginner", "Developing", "Intermediate", "Advanced"][l.level - 1] || `Level ${l.level}`}
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-xl font-medium mb-1" style={{ fontFamily: "Outfit" }}>{l.title}</h3>
                                <p className="text-[#515B57]">{l.objective}</p>
                            </div>
                            <span className="text-sm text-[#2D5D4E] font-medium">+{l.xp} XP</span>
                        </Link>
                    );
                })}
            </div>
        </main>
    );
}
